import { notification, Spin, Flex, Modal } from 'antd';
import PageContainer from './../../common/components/PageContainer';
import ProfileDetails from '../../components/profile/details';
import TeamsCard from '../../components/profile/teamsCard';
import SideDrawer from './../../common/components/sideDrawer/SideDrawer';
import { OwnerTransferForm } from '../../components/profile/OwnerTransferForm';
import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import globalNotification from '../../common/hooks/globalNotification';
import { notificationType } from '../../common/constants/notificationType';
import { useTranslation } from 'react-i18next';
import { useUserService } from '../../services/userService';
import { useMedia } from '../../hooks/useMediaQuery';
import { useTeamService } from '../../services/teamService';
import { notifications } from '../../common/constants/notifications';
import { teamRole } from './../../common/constants/teamRole';
import { spacing, primitiveColors } from './../../theming';
import compareObjectValues from '../../utils/compareObjectValues';
import classes from './style.module.scss';

export default function Profile() {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState({
        delete: false,
        leave: false,
        update: false,
        memberships: false,
    });
    const [userMemberships, setUserMemberships] = useState([]);
    const [clickedTeam, setClickedTeam] = useState(null);
    const { isSmallDevice } = useMedia();

    const { t } = useTranslation(['profile', 'confirmModal', 'form', 'notification']);
    const { currentUser, fetchUser, logout } = useAuth();
    const { updateProfile } = useUserService();
    const { leaveTeam } = useTeamService();
    const { getUserMemberships, deleteTeam } = useTeamService();

    const [api, contextHolder] = notification.useNotification();

    const ownTeams = userMemberships.filter(membership => membership.role === teamRole.OWNER);

    const openNotification = ({ type = 'error', message = '', description = '', placement = 'top' }) => {
        api[type]({
            message: message,
            description: description,
            placement: placement,
        });
    };

    // #region Fetch user memberships
    const fetchUserMemberships = async () => {
        setLoading(prev => ({ ...prev, memberships: true }));

        try {
            const response = await getUserMemberships();

            setUserMemberships(response.data);
        } catch (error) {
            globalNotification.request({
                type: notifications.type.ERROR,
                resource: notifications.resource.TEAMS,
                action: notifications.action.FETCH,
                plural: true,
            });
        } finally {
            setLoading(prev => ({ ...prev, memberships: false }));
        }
    };

    useEffect(() => {
        fetchUserMemberships();
    }, []);
    // #endregion

    // #region Handlers
    const handleOpenDrawer = () => {
        setOpen(prev => !prev);
    };

    const handleSubmit = async values => {
        const fullName = values.fullName || '';
        const [firstName, lastName] = fullName.split(' ');

        const phoneRegex = /^(\+?\d{1,3})?\s?(\(?\d{2,3}\)?)?[\s.-]?\d{2,3}[\s.-]?\d{2}[\s.-]?\d{2}$/;

        if (values.phoneNumber && !phoneRegex.test(values.phoneNumber)) {
            globalNotification.open({
                type: notificationType.ERROR,
                message: t('notification.incorrect-phone-error.message'),
                description: t('notification.incorrect-phone-error.description'),
                placement: 'top',
            });
            return;
        }

        const valuesToSend = {
            firstName: firstName || currentUser.firstName, // If the user doesn't provide a first name, keep the current one. same for last name
            lastName: lastName || currentUser.lastName,
            ...values,
        };
        // We cannot delete first or last name. Form simply won't allow us.

        delete valuesToSend.photo;
        delete valuesToSend.fullName;

        if (compareObjectValues(valuesToSend, currentUser)) {
            return;
        }

        setLoading(prev => ({ ...prev, update: true }));
        try {
            await updateProfile(valuesToSend);

            globalNotification.open({
                type: notificationType.SUCCESS,
                message: t('notification.save-success.message'),
                description: t('notification.save-success.description'),
                placement: 'top',
            });

            fetchUser();
        } catch (err) {
            const errorCode = err.response.data?.code;

            openNotification({
                type: notificationType.ERROR,
                message: t(`apiResponses:${errorCode}.message`) || err.response.data.message,
                description: t(`apiResponses:${errorCode}.description`) || '',
                placement: 'top',
            });
        } finally {
            setLoading(prev => ({ ...prev, update: false }));
        }
    };

    const handleOpenLeaveDrawer = teamId => {
        setClickedTeam(teamId);
        handleOpenDrawer();
    };

    const openDeleteModal = teamId => {
        Modal.confirm({
            title:
                ownTeams.length === 1
                    ? t('notification.delete-last-team.title')
                    : t('notification.delete-confirm.title'),
            icon: null,
            content: (
                <p>
                    {ownTeams.length === 1
                        ? t('notification.delete-last-team.description')
                        : t('notification.delete-confirm.description')}
                </p>
            ),
            onOk: () => handleDeleteTeam(teamId),
            styles: { body: { padding: spacing[4] } },
            width: 'clamp(300px, 85vw, 500px)',
            okButtonProps: { danger: true },
            okText: t('confirmModal:buttons.yes'),
            cancelText: t('confirmModal:buttons.cancel'),
        });
    };

    const handleDeleteTeam = async teamId => {
        setLoading(prev => ({ ...prev, delete: true }));

        try {
            await deleteTeam(teamId);

            globalNotification.open({
                type: notificationType.SUCCESS,
                message: t('notification.delete-success.message'),
                description: t('notification.delete-success.description'),
                placement: 'top',
            });

            fetchUser();
        } catch (err) {
            const errorCode = err.response.data?.code;

            openNotification({
                type: notificationType.ERROR,
                message: t(`apiResponses:${errorCode}.message`),
                description: t(`apiResponses:${errorCode}.description`),
                placement: 'top',
            });
        } finally {
            setLoading(prev => ({ ...prev, delete: false }));
        }
    };

    const handleLeaveTeam = async teamId => {
        setLoading(prev => ({ ...prev, leave: true }));
        try {
            await leaveTeam(teamId);

            if (teamId === currentUser.teamId) {
                logout();

                globalNotification.open({
                    type: notificationType.SUCCESS,
                    message: t('notification.leave-success.message'),
                    description: t('notification.leave-success.description'),
                    placement: 'top',
                });

                return;
            } else {
                fetchUserMemberships();
                fetchUser();
            }

            globalNotification.open({
                type: notificationType.SUCCESS,
                message: t('notification.leave-success.message'),
                description: t('notification.leave-success.description'),
                placement: 'top',
            });
        } catch (err) {
            const errCode = err.response.data?.code;

            openNotification({
                type: notificationType.ERROR,
                message: t(`apiResponses:${errCode}.message`),
                description: t(`apiResponses:${errCode}.description`),
                placement: 'top',
            });
        } finally {
            setLoading(prev => ({ ...prev, leave: false }));
        }
    };

    const handleLeaveClick = (userTeamRole, teamId) => {
        if (userTeamRole === teamRole.OWNER) {
            handleOpenLeaveDrawer(teamId);
        } else {
            Modal.confirm({
                title:
                    userMemberships.length === 1
                        ? t('notification.leave-last-team.title')
                        : t('notification.leave-confirm.title'),
                icon: null,
                content: (
                    <p>
                        {userMemberships.length === 1
                            ? t('notification.leave-last-team.description')
                            : t('notification.leave-confirm.description')}
                    </p>
                ),
                onOk: () => {
                    handleLeaveTeam(teamId);
                },
                styles: { body: { padding: spacing[4] } },
                width: 'fit-content',
                okButtonProps: { danger: true },
                okText: t('confirmModal:buttons.yes'),
                cancelText: t('confirmModal:buttons.cancel'),
            });
        }
    };
    // #endregion

    return (
        <PageContainer
            style={{
                backgroundColor: isSmallDevice ? 'transparent' : primitiveColors.gray0,
                paddingBottom: spacing[5],
            }}
        >
            {contextHolder}
            {loading.memberships ? (
                <Spin />
            ) : (
                <Flex gap={spacing[4]} className={classes.container}>
                    <ProfileDetails currentUser={currentUser} classes={classes} t={t} handler={handleSubmit} />
                    <TeamsCard
                        t={t}
                        userMemberships={userMemberships}
                        currentUser={currentUser}
                        openDeleteModal={openDeleteModal}
                        handleLeaveClick={handleLeaveClick}
                        loading={loading}
                        classes={classes}
                    />
                </Flex>
            )}
            <SideDrawer
                open={open}
                closeDrawer={handleOpenDrawer}
                title={t('form.leave-title')}
                children={
                    <OwnerTransferForm
                        leaveTeam={leaveTeam}
                        fetchUser={fetchUser}
                        fetchUserMemberships={fetchUserMemberships}
                        logout={logout}
                        openNotification={openNotification}
                        closeDrawer={handleOpenDrawer}
                        userMemberships={userMemberships}
                        currentUser={currentUser}
                        teamId={clickedTeam}
                    />
                }
            />
        </PageContainer>
    );
}
